import React, { useEffect, useRef, useState } from "react";
import { ChevronRight } from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Avatar,
  Box,
  Button,
  Divider,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import AiIcon from "assets/icons/ai-icon.svg";
import AiAvatarImage from "assets/icons/ai.png";
import ClientAvatarImage from "assets/icons/client.png";
import LargeScreenIcon from "assets/icons/large-screen.svg";
import NewCrossIcon from "assets/icons/newcross.svg";
import SendIcon from "assets/icons/Send.svg";
import SmallScreenIcon from "assets/icons/small-screen.svg";
import RoomPhotoOne from "assets/rooms/ph-1.png";
import RoomPhotoTwo from "assets/rooms/ph-2.png";
import RoomPhotoThree from "assets/rooms/ph-3.png";
import RoomPhotoFour from "assets/rooms/ph-4.png";
import "./AIAssistantDrawer.css";

const AIAssistantDrawer = ({ selectedAiRow, onClose }) => {
  const [expandedComment, setExpandedComment] = useState("comment-1");
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [messageInput, setMessageInput] = useState("");
  const [chatMessages, setChatMessages] = useState([]);
  const drawerContentRef = useRef(null);
  const tierLabel = String(selectedAiRow?.tier || "A");
  const tierPrefix = tierLabel.trim().charAt(0).toUpperCase() || "A";
  const toPreviewText = (text) => {
    if (!text) return "Tap to view comment details";
    return text.length > 52 ? `${text.slice(0, 52)}...` : text;
  };
  const commentsData = [
    {
      id: "comment-1",
      title: "Comment 1",
      content: selectedAiRow?.sample_note || "No sample note available.",
      subtitle: toPreviewText(selectedAiRow?.sample_note),
    },
    {
      id: "comment-2",
      title: "Comment 2",
      content: selectedAiRow?.result || "No additional comments available.",
      subtitle: toPreviewText(selectedAiRow?.result),
    },
  ];
  const photoItems = [
    { id: "ph-1", src: RoomPhotoOne, alt: "room photo 1" },
    { id: "ph-2", src: RoomPhotoTwo, alt: "room photo 2" },
    { id: "ph-3", src: RoomPhotoThree, alt: "room photo 3" },
    { id: "ph-4", src: RoomPhotoFour, alt: "room photo 4" },
  ];
  useEffect(() => {
    setChatMessages([
      {
        id: "client-initial",
        role: "user",
        text: "Change the classification from Tier A to Tier B.Lorem ipsum dolor sit amet consectetur adipiscing elit sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.",
      },
      {
        id: "ai-initial",
        role: "ai",
        text: `Reviewed the sample details for room ${selectedAiRow?.room_name?.name || "-"} with observation ${selectedAiRow?.observation?.name || "-"}. The recommendation can be added to your knowledge base.`,
      },
    ]);
    setMessageInput("");
  }, [
    selectedAiRow?.id,
    selectedAiRow?.room_name?.name,
    selectedAiRow?.observation?.name,
  ]);

  useEffect(() => {
    if (drawerContentRef.current) {
      drawerContentRef.current.scrollTo({
        top: drawerContentRef.current.scrollHeight,
        behavior: "smooth",
      });
    }
  }, [chatMessages]);

  const handleSendMessage = () => {
    const trimmed = messageInput.trim();
    if (!trimmed) return;

    setChatMessages((prev) => [
      ...prev,
      {
        id: `user-${Date.now()}`,
        role: "user",
        text: trimmed,
      },
    ]);
    setMessageInput("");
  };

  return (
    <Box className={`ai-assistant-panel ${isFullscreen ? "fullscreen" : ""}`}>
      {/* <img
        src={LeftCloseBarIcon}
        className="test-results-close-btn"
        onClick={onClose}
        alt="close"
      /> */}
      <Box className="ai-assistant-header">
        <Box className="ai-assistant-title-pill">
          <img src={AiIcon} alt="AI icon" className="ai-header-icon" />
        </Box>
        <Box className="ai-assistant-header-actions">
          <IconButton
            type="button"
            className="ai-header-action-btn"
            aria-label={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
            onClick={() => setIsFullscreen((prev) => !prev)}
          >
            <img
              src={isFullscreen ? SmallScreenIcon : LargeScreenIcon}
              alt={isFullscreen ? "exit fullscreen" : "fullscreen"}
              className={
                isFullscreen
                  ? "ai-header-action-icon-small"
                  : "ai-header-action-icon-large"
              }
            />
          </IconButton>
          <img
            src={NewCrossIcon}
            className="close-test-results"
            onClick={onClose}
            alt="close"
          />
        </Box>
      </Box>
      <Box className="ai-assistant-content" ref={drawerContentRef}>
        <Box className="ai-context-box">
          <Box className="ai-row-summary">
            <Avatar className="ai-summary-tier">
              <span className="ai-tier-value">{tierPrefix}</span>
              <span className="ai-tier-text">Tier</span>
            </Avatar>
            <Box className="ai-summary-item">
              <Typography component="span">Room</Typography>
              <Typography component="p">
                {selectedAiRow?.room_name?.name || "-"}
              </Typography>
            </Box>
            <Box className="ai-summary-item">
              <Typography component="span">Sample Type</Typography>
              <Typography component="p">
                {selectedAiRow?.test?.name || "-"}
              </Typography>
            </Box>
            <Box className="ai-summary-item">
              <Typography component="span">Observation</Typography>
              <Typography component="p">
                {selectedAiRow?.observation?.name || "-"}
              </Typography>
            </Box>
            <Box
              className={`ai-summary-chip ai-summary-status ${selectedAiRow?.status ? "approved" : "unapproved"}`}
            >
              {selectedAiRow?.status ? "Approved" : "Unapproved"}
            </Box>
          </Box>

          <Divider
            className="ai-row-summary-divider"
            sx={{ borderColor: "#d9d9d9", borderBottomWidth: "medium" }}
          />

          <Box className="ai-section">
            <Typography variant="h4" className="ai-section-title">
              Photos
            </Typography>
            <Box className="ai-photos-grid">
              {photoItems.map((photo) => (
                <Box key={photo.id} className="ai-photo-thumb">
                  <img src={photo.src} alt={photo.alt} />
                </Box>
              ))}
              <Box className="ai-photo-thumb ai-photo-more">
                <Typography component="p" className="ai-photo-more-text">
                  +5 Photos
                </Typography>
              </Box>
            </Box>
          </Box>

          <Box className="ai-section">
            <Typography variant="h4" className="ai-section-title">
              Comments
            </Typography>
            <Box className="ai-comment-accordion">
              {commentsData.map((comment) => (
                <Accordion
                  key={comment.id}
                  expanded={expandedComment === comment.id}
                  onChange={() =>
                    setExpandedComment((prev) =>
                      prev === comment.id ? "" : comment.id
                    )
                  }
                  disableGutters
                  elevation={0}
                  square
                  className="ai-comment-card"
                >
                  <AccordionSummary
                    className="ai-comment-toggle"
                    expandIcon={
                      <ChevronRight
                        className={`ai-chevron ${expandedComment === comment.id ? "expanded" : ""}`}
                      />
                    }
                  >
                    <Box className="ai-comment-heading">
                      <Typography component="h5" className="ai-comment-title">
                        {comment.title}
                      </Typography>
                      <Typography component="p" className="ai-comment-subtext">
                        {comment.subtitle}
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails className="ai-comment-details">
                    <Typography component="p" className="ai-comment-content">
                      {comment.content}
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>
          </Box>

          <Box className="ai-section">
            <Typography variant="h4" className="ai-section-title">
              Reason
            </Typography>
            <Typography component="p" className="ai-reason-content">
              {selectedAiRow?.observation?.name ||
                "No reasoning data available for this sample."}
            </Typography>
          </Box>
        </Box>

        <Box className="ai-analysis-header">
          <span className="ai-analysis-line" />
          <Typography className="ai-analysis-title">
            <img src={AiIcon} alt="AI icon" className="ai-analysis-icon" />
            Analysis
          </Typography>
          <span className="ai-analysis-line" />
        </Box>
        <Box className="ai-chat-wrap">
          {chatMessages.map((message) => (
            <Box key={message.id} className={`ai-chat-row ${message.role}`}>
              {message.role === "ai" ? (
                <>
                  <Avatar
                    src={AiAvatarImage}
                    className="ai-message-avatar ai"
                    variant="rounded"
                  />
                  <Box className="ai-chat-bubble ai">{message.text}</Box>
                </>
              ) : (
                <>
                  <Box className="ai-chat-bubble user">{message.text}</Box>
                  <Avatar
                    src={ClientAvatarImage}
                    className="ai-message-avatar user"
                    variant="rounded"
                  />
                </>
              )}
            </Box>
          ))}
        </Box>
        <Box className="ai-kb-wrap">
          <Button type="button" className="ai-kb-btn">
            Add to Knowledge Base
          </Button>
        </Box>

        <Box className="ai-feedback">
          <TextField
            fullWidth
            variant="standard"
            placeholder="Enter Your Feedback..."
            value={messageInput}
            onChange={(e) => setMessageInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleSendMessage();
              }
            }}
            InputProps={{ disableUnderline: true, readOnly: false }}
          />
          <IconButton
            type="button"
            aria-label="Send feedback"
            onClick={handleSendMessage}
          >
            <img src={SendIcon} alt="send" className="ai-send-icon" />
          </IconButton>
        </Box>
      </Box>
    </Box>
  );
};

export default AIAssistantDrawer;
